import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wrench, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export default function MaintenanceTab() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (settings && !form) {
      setForm({
        active: settings.maintenance?.active || false,
        message: settings.maintenance?.message || "",
        reopen_at: settings.maintenance?.reopen_at || "",
        iscrizioni_close: settings.race?.iscrizioni_close || "",
        lock_at: settings.race?.lock_at || "",
        captain_window_start: settings.race?.captain_window_start || "",
        captain_window_end: settings.race?.captain_window_end || "",
        gara_finita: settings.race?.gara_finita || false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  if (!form) return null;

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        active: form.active,
        message: form.message,
        reopen_at: form.reopen_at || null,
        iscrizioni_close: form.iscrizioni_close || null,
        lock_at: form.lock_at || null,
        captain_window_start: form.captain_window_start || null,
        captain_window_end: form.captain_window_end || null,
        gara_finita: form.gara_finita,
      });
      toast.success(form.active ? "Modalità manutenzione ATTIVA" : "Impostazioni salvate");
      qc.invalidateQueries({ queryKey: ["settings-public"] });
    } catch (e) {
      toast.error(errMsg(e, "Salvataggio fallito"));
    }
  };

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  return (
    <div className="max-w-2xl mt-4 space-y-4">
      <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="w-5 h-5 text-[#FFB703]" />
            <div>
              <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest">
                Modalità Manutenzione
              </h3>
              <p className="text-[10px] text-gray-500">
                Blocca tutte le pagine pubbliche (tranne /admin)
              </p>
            </div>
          </div>
          <Switch
            data-testid="maintenance-toggle"
            checked={form.active}
            onCheckedChange={(v) => setForm({ ...form, active: v })}
          />
        </div>

        <div className="space-y-1.5">
          <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
            Messaggio schermata manutenzione
          </label>
          <textarea
            data-testid="maintenance-message-input"
            className={inputCls}
            rows={3}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              Riapertura prevista (countdown)
            </label>
            <input
              data-testid="maintenance-reopen-input"
              type="datetime-local"
              className={`${inputCls} font-time`}
              value={form.reopen_at ? form.reopen_at.slice(0, 16) : ""}
              onChange={(e) => setForm({ ...form, reopen_at: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              Chiusura iscrizioni squadre
            </label>
            <input
              data-testid="race-close-input"
              type="datetime-local"
              className={`${inputCls} font-time`}
              value={form.iscrizioni_close ? form.iscrizioni_close.slice(0, 16) : ""}
              onChange={(e) => setForm({ ...form, iscrizioni_close: e.target.value })}
            />
          </div>
        </div>

        <div className="rounded-lg border border-[#3F3F4C] bg-[#121214] p-4 space-y-4">
          <p className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
            Calendario Weekend di Gara
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                Blocco formazione (06/11 ore 07:00, inizio Prove)
              </label>
              <input
                data-testid="race-lock-input"
                type="datetime-local"
                className={`${inputCls} font-time`}
                value={form.lock_at ? form.lock_at.slice(0, 16) : ""}
                onChange={(e) => setForm({ ...form, lock_at: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                Finestra Capitano: da (fine Prove)
              </label>
              <input
                data-testid="captain-window-start-input"
                type="datetime-local"
                className={`${inputCls} font-time`}
                value={form.captain_window_start ? form.captain_window_start.slice(0, 16) : ""}
                onChange={(e) => setForm({ ...form, captain_window_start: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                Finestra Capitano: a (inizio Gara)
              </label>
              <input
                data-testid="captain-window-end-input"
                type="datetime-local"
                className={`${inputCls} font-time`}
                value={form.captain_window_end ? form.captain_window_end.slice(0, 16) : ""}
                onChange={(e) => setForm({ ...form, captain_window_end: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[#3F3F4C] px-4 py-3">
            <div>
              <p className="font-body-sport text-xs font-bold text-white uppercase tracking-widest">
                Decreto Fine Competizione
              </p>
              <p className="text-[10px] text-gray-500">
                Finché è disattivo, le classifiche restano UFFICIOSE
              </p>
            </div>
            <Switch
              data-testid="gara-finita-toggle"
              checked={form.gara_finita}
              onCheckedChange={(v) => setForm({ ...form, gara_finita: v })}
            />
          </div>
        </div>

        <button
          data-testid="maintenance-save-button"
          onClick={save}
          className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-5 py-2.5 font-racing font-bold text-xs tracking-widest text-white uppercase"
        >
          <Save className="w-4 h-4" /> Salva Impostazioni
        </button>
      </div>
    </div>
  );
}
